import {type ComponentType, useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import Skeleton from "react-loading-skeleton";
import * as Api from "utils/api/api";
import {DOCUMENT_TYPE} from "utils/constants";
import * as reduxSelectors from "components/store/application.reducers";
import {useTranslation} from "components/translations/useTranslation";
import {getTaskDocumentData} from "components/taskdetails-remake/taskDetails.action";
import type {DocumentDataProps, DocumentDetails, DocumentDetailsProps} from "types/documentData";
import InvoiceDocumentDetailsDTO from "./documentdata/InvoiceDocumentDetailsDTO.component";
import PurchaseOrderDocumentDetailsDTO from "./documentdata/PurchaseOrderDocumentDetailsDTO.component";
import ExpenseDocumentDetailsDTO from "./documentdata/ExpenseDocumentDetailsDTO.component";
import LeaveRequestDocumentDetailsDTO from "./documentdata/LeaveRequestDocumentDetailsDTO.component";
import TimesheetDocumentDetailsDTO from "./documentdata/TimesheetDocumentDetailsDTO.component";
import VoucherDocumentDetailsDTO from "./documentdata/VoucherDocumentDetailsDTO.component";
import WorkflowDocumentDetailsDTO from "./documentdata/WorkflowDocumentDetailsDTO.component";
import SupplierDocumentDetailsDTO from "./documentdata/SupplierDocumentDetailsDTO.component";
import DocumentDataDefault from "./documentdata/DocumentDataDefault.component";
import {TopWarnings} from "./documentdata/DocumentWarnings.component";
import {formatWarnings} from "./documentdata/formatWarnings";
import "../style/documentData.scss";

// the fields of each document come from the DTO the backend names in xtype
// (CostRequest is the purchase order's DTO under its older name)
const DOCUMENT_DETAILS_BY_XTYPE: Record<string, ComponentType<DocumentDetailsProps>> = {
    CostRequestDocumentDetailsDTO: PurchaseOrderDocumentDetailsDTO,
    PurchaseOrderDocumentDetailsDTO: PurchaseOrderDocumentDetailsDTO,
    ExpenseDocumentDetailsDTO: ExpenseDocumentDetailsDTO,
    InvoiceDocumentDetailsDTO: InvoiceDocumentDetailsDTO,
    LeaveRequestDocumentDetailsDTO: LeaveRequestDocumentDetailsDTO,
    TimesheetDocumentDetailsDTO: TimesheetDocumentDetailsDTO,
    VoucherDocumentDetailsDTO: VoucherDocumentDetailsDTO,
    WorkflowDocumentDetailsDTO: WorkflowDocumentDetailsDTO,
    SupplierDocumentDetailsDTO: SupplierDocumentDetailsDTO,
};

const SKELETON_ROWS = 4;

/**
 * The voucher details module of the redesigned task details page: the document's own data, titled
 * after its type, with the fields of its DTO laid out in a grid of label-over-value pairs.
 * It replaces taskdetails-remake/panels/DocumentData.component on the redesigned page.
 */
export const DocumentData = ({taskId, documentType, isDocument, readOnly}: DocumentDataProps) => {
    const dispatch = useDispatch();
    const {translate} = useTranslation();
    const documentData: DocumentDetails | null = useSelector(reduxSelectors.getTaskDocumentData);
    const loading: boolean = useSelector(reduxSelectors.getTaskDocumentDataLoadingStatus);
    const userLanguage: string = useSelector(reduxSelectors.getLanguage);
    const dateFormat: string = useSelector(reduxSelectors.getDateFormatForLanguage);

    // the task on screen, or null once the panel is gone: a save that completes later only reloads
    // its own task while it is still shown, so it cannot put its task's data over another task's
    const activeTaskIdRef = useRef<string | null>(taskId);

    useEffect(() => {
        activeTaskIdRef.current = taskId;
        dispatch(getTaskDocumentData(taskId, isDocument));
        return () => {
            activeTaskIdRef.current = null;
        };
    }, [dispatch, taskId, isDocument]);

    const saveDocumentData = (data: DocumentDetails) => {
        Api.postTaskDocumentDetails(taskId, data).then(() => {
            if (activeTaskIdRef.current === taskId) {
                dispatch(getTaskDocumentData(taskId, isDocument));
            }
        });
    };

    const DocumentDetailsType = documentData
        ? DOCUMENT_DETAILS_BY_XTYPE[documentData.xtype || ""] || DocumentDataDefault
        : null;
    const warnings = documentData ? formatWarnings(documentData.warnings, Object.keys(documentData)) : {};
    const topWarnings = warnings[""];
    const title = translate("taskDetail.details", translate("documentType." + DOCUMENT_TYPE.asString(documentType)));

    return (
        <section className="document-data" data-cy="taskDetail.detailsPanel" aria-labelledby="document-data-title">
            <header className="document-data__header">
                <h2 id="document-data-title" className="document-data__title">{title}</h2>
            </header>
            <div id="document-data" className="document-data__body">
                {topWarnings && <TopWarnings warnings={topWarnings}/>}
                {loading ?
                    <div className="document-data__loading" data-cy="taskDetail.detailsPanel.loading">
                        {Array.from({length: SKELETON_ROWS}, (_, index) =>
                            <div key={index}>
                                <Skeleton className="document-data__loading-label"/>
                                <Skeleton className="document-data__loading-value"/>
                            </div>)}
                    </div> :
                    DocumentDetailsType && documentData &&
                    <DocumentDetailsType details={documentData}
                                         warnings={warnings}
                                         translate={translate}
                                         userLanguage={userLanguage}
                                         dateFormat={dateFormat}
                                         saveData={saveDocumentData}
                                         readOnly={readOnly}/>}
            </div>
        </section>
    );
};

export default DocumentData;
